import { eventBus } from '../../../core/events/index.js';
import { HangoutState, HANGOUT_EVENTS } from '../state/HangoutState.js';
import { getBackgroundService } from '../../../core/services/BackgroundService.js';
import { backgroundRenderer } from '../../../core/services/BackgroundRenderer.js';
import { storage } from '../../../js/utils/storage.js';
import { storageManager } from '../../../core/storage/index.js';
import { getTimeDefinitionService } from '../../../core/services/TimeDefinitionService.js';
import {
  parseClockHour,
  resolvePreservedClockHour,
  shouldPreserveLocalBackdrop
} from '../shared/session-backdrop.js';

export class TimeProgressionService {
  static instance = null;

  static getInstance() {
    if (!TimeProgressionService.instance) {
      TimeProgressionService.instance = new TimeProgressionService();
    }
    return TimeProgressionService.instance;
  }

  constructor() {
    this.hangoutState = HangoutState.getInstance();
    this.currentTime = 20;
    this._isRunning = false;
    this.progressionInterval = null;
    this.sessionStartTime = null;
    this.lastProgressionTime = null;

    this.PROGRESSION_RATE = 10 * 60000;
    this.STORAGE_KEY_PREFIX = 'hangout_time_';

    this._subscribeToEvents();
    
    if (this._isInHangoutPage()) {
      this._checkAndRestoreState().catch(console.warn);
    }
  }

  _subscribeToEvents() {
    this.hangoutState.subscribe(HANGOUT_EVENTS.SESSION_STARTED, () => this.start().catch(console.warn));
    this.hangoutState.subscribe(HANGOUT_EVENTS.SESSION_ENDED, () => this.stop());
  }

  _isInHangoutPage() {
    return typeof window !== 'undefined' && 
      (window.location.pathname.includes('/hangout') || window.location.pathname.includes('hangout.html'));
  }

  async _checkAndRestoreState() {
    if (!this._isInHangoutPage() || this._isRunning) return;

    const session = this.hangoutState.getCurrentSession();
    const conversationId = this.hangoutState.getConversationId();

    if (session && conversationId) {
      await this.start();
    }
  }

  async start() {
    if (!this._isInHangoutPage() || this._isRunning) return;

    this._isRunning = true;
    this.sessionStartTime = Date.now();
    this.lastProgressionTime = Date.now();

    await this._initializeTime();
    this.progressionInterval = setInterval(() => this._progressTime(), this.PROGRESSION_RATE);
  }

  stop() {
    if (!this._isRunning) return;

    this._isRunning = false;
    if (this.progressionInterval) {
      clearInterval(this.progressionInterval);
      this.progressionInterval = null;
    }

    this._saveTimeState();
    this._clearState();
  }

  _progressTime() {
    const now = Date.now();
    if (now - this.lastProgressionTime >= this.PROGRESSION_RATE * 0.9) {
      this.currentTime = (this.currentTime + 1) % 24;
      this.lastProgressionTime = now;
      this._saveTimeState();
      this._emitTimeUpdate().catch(console.warn);
    }
  }

  primeFromStoredConfig() {
    if (this._isRunning) return;

    const config = storage.get('currentHangoutConfig') || {};
    const hour = shouldPreserveLocalBackdrop(config)
      ? resolvePreservedClockHour(config, this._readManualHour())
      : parseClockHour(config.timeHour);
    if (hour == null) return;
    this.currentTime = hour;
  }

  adoptScenarioHour(hour) {
    const parsed = parseClockHour(hour);
    if (parsed == null || this.currentTime === parsed) return;
    this.currentTime = parsed;
    if (this._isRunning) {
      this._emitTimeUpdate().catch(console.warn);
    }
  }

  async _initializeTime() {
    const config = storage.get('currentHangoutConfig') || {};

    if (shouldPreserveLocalBackdrop(config)) {
      if (this._loadConversationTimeState()) return;
      const hour = resolvePreservedClockHour(config, this._readManualHour());
      if (hour != null) this.currentTime = hour;
      this._emitTimeUpdate().catch(console.warn);
      return;
    }

    const seededHour = parseClockHour(config.timeHour);
    if (seededHour != null) {
      this.currentTime = seededHour;
      this._emitTimeUpdate().catch(console.warn);
      return;
    }

    if (!config.isNewConversation && this._loadConversationTimeState()) return;

    const scenarioTime = this._getScenarioStartTime();
    const manualHour = !config.isNewConversation ? this._readManualHour() : null;
    if (scenarioTime != null) {
      this.currentTime = scenarioTime;
    } else if (manualHour != null) {
      this.currentTime = manualHour;
    } else {
      this.currentTime = 20;
    }

    this._emitTimeUpdate().catch(console.warn);
  }

  _readManualHour() {
    const override = storage.get('hangout_user_time_override');
    if (override) {
      const hour = parseInt(override);
      if (hour >= 0 && hour < 24) return hour;
    }

    if (storage.get('hangout_manual_time_set') !== 'true') return null;

    const slot = this.hangoutState.getCurrentTimeSlot();
    const timeService = getTimeDefinitionService();
    const timeOfDay = timeService.getTimeOfDayFromNumericSlot(slot) || slot;
    return timeService.getDefaultHour(timeOfDay);
  }

  _getScenarioStartTime() {
    const session = this.hangoutState.getCurrentSession();
    const scenarioId = session?.scenarioId;
    if (!scenarioId) return null;

    const scenarios = this.hangoutState.getScenarios();
    let scenario = scenarios.find(s => String(s.id) === String(scenarioId));

    if (!scenario) {
      scenario = this.hangoutState.getSelectedScenario();
      if (!scenario || String(scenario.id) !== String(scenarioId)) return null;
    }

    const parsedStartTime = this._parseStartTime(scenario.startTime);
    if (parsedStartTime !== null) return parsedStartTime;

    if (scenario.time !== undefined && scenario.time !== null) {
      const timeService = getTimeDefinitionService();
      return timeService.getDefaultHour(timeService.getTimeOfDayFromNumericSlot(scenario.time));
    }

    return null;
  }

  _parseStartTime(startTime) {
    if (!startTime && startTime !== 0) return null;

    let hour;
    if (typeof startTime === 'string') {
      hour = startTime.includes(':') ? parseInt(startTime.split(':')[0]) : parseInt(startTime);
    } else {
      hour = startTime;
    }

    return (hour >= 0 && hour < 24) ? hour : null;
  }

  getFormattedTime() {
    return `${this.currentTime.toString().padStart(2, '0')}:00`;
  }

  getCurrentTime() {
    return this.currentTime;
  }

  get isRunning() {
    return this._isRunning;
  }

  getElapsedMinutes() {
    return this.sessionStartTime ? Math.floor((Date.now() - this.sessionStartTime) / 60000) : 0;
  }

  async _persistTimeHour() {
    try {
      const config = await storageManager.getItem('currentHangoutConfig') || {};
      if (config.timeHour === this.currentTime) return;
      config.timeHour = this.currentTime;
      await storageManager.setItem('currentHangoutConfig', config);
    } catch (error) {
      console.warn('[TimeProgressionService] Failed to persist time hour:', error);
    }
  }

  async _emitTimeUpdate() {
    try {
      await this._persistTimeHour();
      const backgroundService = getBackgroundService();
      let timeOfDay = 'night';
      
      if (this.currentTime >= 5 && this.currentTime < 17) {
        timeOfDay = 'day';
      } else if (this.currentTime >= 17 && this.currentTime < 20) {
        timeOfDay = 'sunset';
      }
      
      const currentBackground = await backgroundService.setTimeOfDay(timeOfDay);
      if (currentBackground?.backgroundUrl) {
        await backgroundRenderer.applyBackgroundImage(currentBackground.backgroundUrl);
      }
    } catch (error) {
      console.warn('[TimeProgressionService] BackgroundService error:', error);
    }

    eventBus.emit('hangout:time:updated', {
      hour: this.currentTime,
      time: this.currentTime,
      formatted: this.getFormattedTime(),
      elapsedMinutes: this.getElapsedMinutes()
    });
  }

  _saveTimeState() {
    const conversationId = this.hangoutState.getConversationId();
    if (!conversationId) return;

    const timeState = {
      currentTime: this.currentTime,
      lastProgressionTime: this.lastProgressionTime
    };

    storage.set(`${this.STORAGE_KEY_PREFIX}${conversationId}`, timeState);
  }

  _loadConversationTimeState() {
    const conversationId = this.hangoutState.getConversationId();
    if (!conversationId) return false;

    const state = storage.get(`${this.STORAGE_KEY_PREFIX}${conversationId}`);
    if (!state || state.currentTime === undefined) return false;

    this.currentTime = state.currentTime;
    this.lastProgressionTime = Date.now();
    this._emitTimeUpdate().catch(console.warn);
    return true;
  }

  _clearState() {
    const conversationId = this.hangoutState.getConversationId();
    if (conversationId) {
      storage.remove(`${this.STORAGE_KEY_PREFIX}${conversationId}`);
    }
  }

  async setHour(hour) {
    if (hour < 0 || hour >= 24) return;

    storage.set('hangout_user_time_override', hour.toString());
    storage.set('hangout_manual_time_set', 'true');

    this.currentTime = hour;
    this.lastProgressionTime = Date.now();
    this._saveTimeState();
    await this._emitTimeUpdate();
  }
}

export const timeProgressionService = TimeProgressionService.getInstance();

if (typeof window !== 'undefined') {
  window.timeProgressionService = timeProgressionService;
}
